<script setup>
import { ref, computed } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useCartStore } from '@/stores/cart'
import AppLogo from '@/components/common/AppLogo.vue'
import {
  Bars3Icon,
  XMarkIcon,
  ShoppingCartIcon,
  UserCircleIcon,
  ChevronDownIcon,
} from '@heroicons/vue/24/outline'
import {
  Menu,
  MenuButton,
  MenuItems,
  MenuItem,
} from '@headlessui/vue'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const cartStore = useCartStore()

async function handleLogout() {
  await authStore.signOut()
  router.push({ name: 'home' })
}

const mobileMenuOpen = ref(false)

const navigation = [
  { name: 'Home', route: 'home' },
  { name: 'Sobre', route: 'about' },
  { name: 'Serviços', route: 'services' },
  { name: 'Notícias', route: 'news-list' },
  { name: 'Loja', route: 'shop' },
  { name: 'Contato', route: 'contact' },
]

const isScrolled = ref(false)

// Listener para scroll
if (typeof window !== 'undefined') {
  window.addEventListener('scroll', () => {
    isScrolled.value = window.scrollY > 20
  })
}

const headerClasses = computed(() => {
  return [
    'fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white',
    isScrolled.value
      ? 'shadow-md py-2'
      : 'shadow-sm py-4'
  ]
})

const logoVariant = computed(() => 'default')

const linkClasses = computed(() => {
  return (routeName) => {
    const isActive = route.name === routeName
    const baseClasses = 'px-3 py-2 text-sm font-medium transition-colors duration-200 relative'

    if (isActive) {
      return `${baseClasses} text-orange-500 font-semibold`
    }

    return `${baseClasses} text-primary-700 hover:text-orange-500`
  }
})

const iconClasses = computed(() => 'text-primary-700 hover:text-orange-500')

function toggleMobileMenu() {
  mobileMenuOpen.value = !mobileMenuOpen.value
}

function closeMobileMenu() {
  mobileMenuOpen.value = false
}
</script>

<template>
  <header :class="headerClasses">
    <div class="container-custom">
      <div class="flex items-center justify-between">
        <!-- Logo -->
        <RouterLink to="/" class="flex items-center">
          <AppLogo :variant="logoVariant" size="sm" />
        </RouterLink>

        <!-- Desktop Navigation -->
        <nav class="hidden lg:flex items-center gap-1">
          <RouterLink
            v-for="item in navigation"
            :key="item.route"
            :to="{ name: item.route }"
            :class="linkClasses(item.route)"
          >
            {{ item.name }}
            <span
              v-if="route.name === item.route"
              class="absolute left-3 right-3 -bottom-0.5 h-0.5 bg-orange-500 rounded-full"
            ></span>
          </RouterLink>
        </nav>

        <!-- Right side actions -->
        <div class="flex items-center gap-2">
          <!-- Cart button -->
          <button
            @click="cartStore.openDrawer"
            :class="['relative p-2 rounded-lg transition-colors duration-200', iconClasses]"
          >
            <ShoppingCartIcon class="w-6 h-6" />
            <span
              v-if="cartStore.itemCount > 0"
              class="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center"
            >
              {{ cartStore.itemCount }}
            </span>
          </button>

          <!-- User menu (authenticated) -->
          <Menu v-if="authStore.isAuthenticated" as="div" class="relative">
            <MenuButton :class="['flex items-center gap-1 p-2 rounded-lg transition-colors duration-200', iconClasses]">
              <UserCircleIcon class="w-6 h-6" />
              <ChevronDownIcon class="w-4 h-4" />
            </MenuButton>
            
            <transition
              enter-active-class="transition duration-100 ease-out"
              enter-from-class="transform scale-95 opacity-0"
              enter-to-class="transform scale-100 opacity-100"
              leave-active-class="transition duration-75 ease-in"
              leave-from-class="transform scale-100 opacity-100"
              leave-to-class="transform scale-95 opacity-0"
            >
              <MenuItems class="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 ring-1 ring-black ring-opacity-5 focus:outline-none">
                <MenuItem v-slot="{ active }">
                  <RouterLink
                    to="/cliente"
                    :class="[
                      active ? 'bg-gray-100' : '',
                      'block px-4 py-2 text-sm text-gray-700'
                    ]"
                  >
                    Minha Conta
                  </RouterLink>
                </MenuItem>
                <MenuItem v-slot="{ active }">
                  <RouterLink
                    to="/cliente/produtos"
                    :class="[
                      active ? 'bg-gray-100' : '',
                      'block px-4 py-2 text-sm text-gray-700'
                    ]"
                  >
                    Meus Produtos
                  </RouterLink>
                </MenuItem>
                <MenuItem v-slot="{ active }">
                  <RouterLink
                    to="/cliente/pedidos"
                    :class="[
                      active ? 'bg-gray-100' : '',
                      'block px-4 py-2 text-sm text-gray-700'
                    ]"
                  >
                    Meus Pedidos
                  </RouterLink>
                </MenuItem>
                <hr class="my-1">
                <MenuItem v-slot="{ active }">
                  <button
                    @click="handleLogout"
                    :class="[
                      active ? 'bg-gray-100' : '',
                      'block w-full text-left px-4 py-2 text-sm text-red-600'
                    ]"
                  >
                    Sair
                  </button>
                </MenuItem>
              </MenuItems>
            </transition>
          </Menu>

          <!-- Login button (not authenticated) -->
          <RouterLink
            v-else
            to="/login"
            :class="['hidden sm:flex items-center gap-1 px-4 py-2 rounded-lg transition-colors duration-200', iconClasses]"
          >
            <UserCircleIcon class="w-5 h-5" />
            <span class="text-sm font-medium">Entrar</span>
          </RouterLink>

          <!-- Mobile menu button -->
          <button
            @click="toggleMobileMenu"
            :class="['lg:hidden p-2 rounded-lg transition-colors duration-200', iconClasses]"
          >
            <Bars3Icon v-if="!mobileMenuOpen" class="w-6 h-6" />
            <XMarkIcon v-else class="w-6 h-6" />
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile menu -->
    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 -translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-2"
    >
      <div v-if="mobileMenuOpen" class="lg:hidden bg-white border-t shadow-lg">
        <nav class="container-custom py-4 space-y-1">
          <RouterLink
            v-for="item in navigation"
            :key="item.route"
            :to="{ name: item.route }"
            :class="[
              'block px-4 py-3 rounded-lg transition-colors',
              route.name === item.route
                ? 'text-orange-500 font-semibold bg-orange-50'
                : 'text-primary-700 hover:bg-primary-50'
            ]"
            @click="closeMobileMenu"
          >
            {{ item.name }}
          </RouterLink>

          <hr class="my-2">

          <template v-if="authStore.isAuthenticated">
            <RouterLink
              to="/cliente"
              class="block px-4 py-3 text-primary-700 hover:bg-primary-50 rounded-lg"
              @click="closeMobileMenu"
            >
              Minha Conta
            </RouterLink>
            <button
              @click="handleLogout(); closeMobileMenu()"
              class="block w-full text-left px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg"
            >
              Sair
            </button>
          </template>
          <template v-else>
            <RouterLink
              to="/login"
              class="block px-4 py-3 text-primary-700 hover:bg-primary-50 rounded-lg font-medium"
              @click="closeMobileMenu"
            >
              Entrar
            </RouterLink>
            <RouterLink
              to="/registro"
              class="block px-4 py-3 bg-orange-500 text-white hover:bg-orange-600 rounded-lg font-medium text-center"
              @click="closeMobileMenu"
            >
              Criar Conta
            </RouterLink>
          </template>
        </nav>
      </div>
    </transition>
  </header>
</template>
